import { useEffect, useMemo, useRef, useState } from "react";
import { Bell, BellOff, Calendar, Clock, Repeat } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { MultiSelect, type MultiSelectOption } from "@/components/ui/MultiSelect";
import {
  DEFAULT_TAREFA_NOTIFICACOES,
  EMPTY_TAREFA_FORM,
  fetchTarefaUsuarios,
  TAREFA_STATUS_LABELS,
  type TarefaDto,
  type TarefaFormValues,
  type TarefaGrupoDto,
  type TarefaNotificacoes,
  type TarefaStatus,
  type TarefaTipo,
  type TarefaUsuarioOption,
} from "@/services/tarefas";
import { cn } from "@/utils/cn";
import { TarefaTimelineModal } from "./TarefaTimelineModal";
import { ModalRecorrenciaCustom } from "./ModalRecorrenciaCustom";
import {
  buildRecorrenciaPresetOptions,
  DEFAULT_RECORRENCIA_FORM,
  formToRecorrencia,
  recorrenciaResumo,
  recorrenciaToForm,
  type RecorrenciaFormState,
  type RecorrenciaPreset,
} from "./recorrenciaUtils";

interface ModalNovaTarefaProps {
  open: boolean;
  data: string;
  editing: TarefaDto | null;
  defaultTipo?: TarefaTipo;
  grupos: TarefaGrupoDto[];
  busy?: boolean;
  onClose: () => void;
  onSave: (values: TarefaFormValues, editingId: string | null) => Promise<void>;
  onDelete?: () => void;
}

const STATUS_OPTIONS: TarefaStatus[] = [
  "pendente",
  "em_andamento",
  "parada",
  "concluida",
];

const NOTIF_OPTIONS: Array<{ key: keyof TarefaNotificacoes; label: string; desc: string }> = [
  {
    key: "aoSalvar",
    label: "Ao criar / editar",
    desc: "Envia WhatsApp para todos os envolvidos ao salvar",
  },
  {
    key: "manha7h",
    label: "Resumo matinal (07:00)",
    desc: "Aviso às 07:00 com todos os eventos do dia",
  },
  {
    key: "antes30min",
    label: "30 min antes",
    desc: "Cadastra WhatsApp automático para os envolvidos 30 minutos antes",
  },
];

export function ModalNovaTarefa({
  open,
  data,
  editing,
  defaultTipo = "tarefa",
  grupos,
  busy = false,
  onClose,
  onSave,
  onDelete,
}: ModalNovaTarefaProps) {
  const [values, setValues] = useState<TarefaFormValues>(EMPTY_TAREFA_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [usuarios, setUsuarios] = useState<TarefaUsuarioOption[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [recForm, setRecForm] = useState<RecorrenciaFormState>(DEFAULT_RECORRENCIA_FORM);
  const [customRecOpen, setCustomRecOpen] = useState(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;

    if (editing) {
      const rec = recorrenciaToForm(editing.recorrencia);
      setRecForm(rec);
      setValues({
        tipo: editing.tipo ?? "tarefa",
        titulo: editing.titulo,
        descricao: editing.descricao,
        data: editing.data,
        horario: editing.horario ?? "",
        horarioFim: editing.horarioFim ?? "",
        grupoId: editing.grupoId ?? "",
        notificacoes: editing.notificacoes ?? { ...DEFAULT_TAREFA_NOTIFICACOES },
        status: editing.status,
        usuarioIds: editing.usuarioIds ?? [],
        recorrencia: editing.recorrencia ?? null,
      });
    } else {
      setRecForm(DEFAULT_RECORRENCIA_FORM);
      setValues({ ...EMPTY_TAREFA_FORM, data, tipo: defaultTipo });
    }
    setErrors({});
    setTimelineOpen(false);
  }, [open, editing, data, defaultTipo]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingUsuarios(true);
    fetchTarefaUsuarios()
      .then((list) => { if (!cancelled) setUsuarios(list); })
      .catch(() => { if (!cancelled) setUsuarios([]); })
      .finally(() => { if (!cancelled) setLoadingUsuarios(false); });
    return () => { cancelled = true; };
  }, [open]);

  const usuarioOptions: MultiSelectOption[] = useMemo(
    () =>
      usuarios.map((u) => ({
        value: u.id,
        label: u.hasPhone ? u.displayName : `${u.displayName} (sem WhatsApp)`,
      })),
    [usuarios],
  );

  const selectedWithoutPhone = useMemo(() => {
    const map = new Map(usuarios.map((u) => [u.id, u]));
    return values.usuarioIds.filter((id) => !map.get(id)?.hasPhone);
  }, [values.usuarioIds, usuarios]);

  const grupoOptions = useMemo(
    () => [
      { value: "", label: "Sem grupo" },
      ...grupos.map((g) => ({ value: g.id, label: g.nome })),
    ],
    [grupos],
  );

  const isEvento = values.tipo === "evento";
  const hasHorario = Boolean(values.horario);

  const recorrenciaOptions = useMemo(
    () => buildRecorrenciaPresetOptions(values.data || data),
    [values.data, data],
  );

  const applyRecForm = (next: RecorrenciaFormState) => {
    setRecForm(next);
    setValues((v) => ({ ...v, recorrencia: formToRecorrencia(next) }));
  };

  const handlePresetChange = (preset: RecorrenciaPreset) => {
    if (preset === "custom") {
      setCustomRecOpen(true);
      applyRecForm({ ...recForm, preset: "custom" });
      return;
    }
    applyRecForm({
      preset,
      freq: preset === "none" ? "none" : preset,
      interval: 1,
      until: recForm.until,
    });
  };

  const selectedPreset =
    recForm.preset === "custom"
      ? "custom"
      : recorrenciaOptions.find((o) => o.value === recForm.preset)?.value ?? "none";

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!values.titulo.trim()) next.titulo = "Informe o título.";
    if (!values.data) next.data = "Informe a data.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(values, editing?.id ?? null);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const setNotif = (key: keyof TarefaNotificacoes, val: boolean) =>
    setValues((v) => ({ ...v, notificacoes: { ...v.notificacoes, [key]: val } }));

  const title = editing
    ? values.tipo === "evento"
      ? "Editar evento"
      : "Editar tarefa"
    : values.tipo === "evento"
      ? "Novo evento"
      : "Nova tarefa";
  const hasTimeline = (editing?.timeline?.length ?? 0) > 0;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={title}
        size="md"
        footer={
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {onDelete && (
                <Button variant="danger" size="sm" loading={busy} onClick={onDelete}>
                  Excluir
                </Button>
              )}
              {editing && hasTimeline && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  leftIcon={<Clock className="h-4 w-4" />}
                  onClick={() => setTimelineOpen(true)}
                >
                  Ver leadtime
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button loading={saving} onClick={handleSubmit}>
                {editing
                  ? "Salvar alterações"
                  : isEvento
                    ? "Criar evento"
                    : "Criar tarefa"}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Tipo: Tarefa | Evento */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Tipo
            </label>
            <div className="flex gap-2">
              {(["tarefa", "evento"] as TarefaTipo[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setValues((v) => ({ ...v, tipo: t }))}
                  className={cn(
                    "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
                    values.tipo === t
                      ? t === "evento"
                        ? "border-violet-300 bg-violet-50 text-violet-700"
                        : "border-blue-300 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50",
                  )}
                >
                  {t === "evento" ? (
                    <Calendar className="h-3.5 w-3.5" />
                  ) : (
                    <Clock className="h-3.5 w-3.5" />
                  )}
                  {t === "evento" ? "Evento" : "Tarefa"}
                </button>
              ))}
            </div>
          </div>

          <Input
            label="Título"
            value={values.titulo}
            onChange={(e) => setValues((v) => ({ ...v, titulo: e.target.value }))}
            error={errors.titulo}
            placeholder={isEvento ? "Ex.: Reunião de obra" : "Ex.: Entregar água do Riviera"}
            autoFocus
          />

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Descrição
            </label>
            <textarea
              value={values.descricao}
              onChange={(e) => setValues((v) => ({ ...v, descricao: e.target.value }))}
              rows={3}
              placeholder="Detalhes (opcional)"
              className="w-full resize-y rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600"
            />
          </div>

          {/* Data + Horários */}
          <div className={cn("grid gap-4", isEvento ? "grid-cols-3" : "grid-cols-2")}>
            <Input
              label="Data"
              type="date"
              value={values.data}
              onChange={(e) => setValues((v) => ({ ...v, data: e.target.value }))}
              error={errors.data}
            />
            <Input
              label={isEvento ? "Hora início (opcional)" : "Horário (opcional)"}
              type="time"
              value={values.horario}
              onChange={(e) => {
                const horario = e.target.value;
                setValues((v) => {
                  const addedHorario = !v.horario && !!horario;
                  return {
                    ...v,
                    horario,
                    notificacoes:
                      addedHorario
                        ? { ...v.notificacoes, antes30min: true }
                        : v.notificacoes,
                  };
                });
              }}
            />
            {isEvento && (
              <Input
                label="Hora fim (opcional)"
                type="time"
                value={values.horarioFim}
                onChange={(e) => setValues((v) => ({ ...v, horarioFim: e.target.value }))}
              />
            )}
          </div>

          <Select
            label="Repetir"
            value={selectedPreset}
            onChange={(e) => handlePresetChange(e.target.value as RecorrenciaPreset)}
            options={recorrenciaOptions.map((o) => ({ value: o.value, label: o.label }))}
          />
          {values.recorrencia && (
            <p className="-mt-2 flex items-center gap-1.5 text-xs text-violet-600">
              <Repeat className="h-3.5 w-3.5" />
              {recorrenciaResumo(values.recorrencia, values.data || data)}
            </p>
          )}

          {/* Grupo de tarefas */}
          <Select
            label="Grupo de tarefas (opcional)"
            value={values.grupoId}
            onChange={(e) => setValues((v) => ({ ...v, grupoId: e.target.value }))}
            options={grupoOptions}
          />
          {grupos.length === 0 && (
            <p className="text-xs text-slate-400">
              Crie grupos na barra lateral para organizar por obra, equipe ou escritório.
            </p>
          )}

          <MultiSelect
            label="Compartilhar com"
            value={values.usuarioIds}
            onChange={(usuarioIds) => setValues((v) => ({ ...v, usuarioIds }))}
            options={usuarioOptions}
            placeholder="Selecione um ou mais usuários"
            loading={loadingUsuarios}
            dropdownMinWidth={320}
          />
          {selectedWithoutPhone.length > 0 && (
            <p className="text-xs text-amber-600">
              Alguns usuários não têm telefone WhatsApp e não receberão notificação.
            </p>
          )}

          <Select
            label="Status"
            value={values.status}
            onChange={(e) =>
              setValues((v) => ({ ...v, status: e.target.value as TarefaStatus }))
            }
            options={STATUS_OPTIONS.map((s) => ({
              value: s,
              label: TAREFA_STATUS_LABELS[s],
            }))}
          />

          {/* Notificações */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Notificações WhatsApp
            </label>
            <div className="space-y-1.5 rounded-lg border border-slate-100 bg-slate-50 p-3">
              {NOTIF_OPTIONS.map(({ key, label, desc }) => {
                const active = values.notificacoes[key];
                const disabled =
                  (key === "manha7h" && !isEvento) ||
                  (key === "antes30min" && !hasHorario);
                const disabledTitle =
                  key === "antes30min"
                    ? "Informe um horário para cadastrar o aviso de 30 minutos"
                    : "Disponível apenas para eventos";
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && setNotif(key, !active)}
                    className={cn(
                      "flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left transition-colors",
                      disabled
                        ? "cursor-not-allowed opacity-40"
                        : active
                          ? "bg-blue-50 text-blue-800"
                          : "bg-white text-slate-500 hover:bg-slate-100",
                    )}
                    title={disabled ? disabledTitle : undefined}
                  >
                    {active && !disabled ? (
                      <Bell className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                    ) : (
                      <BellOff className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                    )}
                    <div>
                      <p className="text-sm font-medium leading-tight">{label}</p>
                      <p className="mt-0.5 text-xs text-slate-400">{desc}</p>
                    </div>
                  </button>
                );
              })}
              <p className="mt-1 text-[11px] text-slate-400">
                Com horário, o aviso de 30 minutos é cadastrado automaticamente para o criador e os usuários compartilhados.
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {editing && (
        <TarefaTimelineModal
          open={timelineOpen}
          onClose={() => setTimelineOpen(false)}
          tarefa={editing}
        />
      )}

      <ModalRecorrenciaCustom
        open={customRecOpen}
        initial={recForm.preset === "custom" ? recForm : { ...recForm, preset: "custom", freq: "weekly" }}
        onClose={() => setCustomRecOpen(false)}
        onSave={(form) => applyRecForm(form)}
      />
    </>
  );
}
